/**
 * AdminKpiCard — Uniforme KPI-tegel voor het admin backoffice.
 *
 * Vervangt de losse KpiCard-implementaties in DebiteurenDashboard.jsx,
 * BetalingsregelingenSection.jsx, DashboardSection.jsx etc.  Elke KPI-card
 * heeft nu identieke:
 *   - Hoogte (h-full via flexbox)
 *   - Padding
 *   - Border-radius
 *   - Iconafhandeling (rechtsboven i.p.v. onderaan → visueel rustiger)
 *   - Tonen-schema (7 kleuren)
 *   - Skeleton loading-state
 *   - Optionele trend-indicator (▲/▼ %)
 *
 * Props:
 *   label        string
 *   value        string | number  (of undefined tijdens skeleton)
 *   icon         Lucide component
 *   tone         "slate" | "brand" | "emerald" | "amber" | "orange" | "red" | "darkred" | "blue"
 *   sublabel     string  (optioneel, kleine hint onder waarde)
 *   trend        { direction: "up"|"down"|"flat", pct: number, label: string }
 *   loading      boolean  (skeleton)
 *   onClick      function  (optioneel — clickable card)
 *   testid       string
 */
import React from "react";

const TONE_STYLES = {
  slate:   { card: "bg-white border-slate-200",                text: "text-slate-900",  label: "text-ink-500",   icon: "bg-slate-100 text-slate-500" },
  brand:   { card: "bg-brand-50 border-brand-100",             text: "text-brand-900",  label: "text-brand-700", icon: "bg-brand-100 text-brand-600" },
  emerald: { card: "bg-emerald-50 border-emerald-100",         text: "text-emerald-900",label: "text-emerald-700",icon:"bg-emerald-100 text-emerald-600"},
  amber:   { card: "bg-amber-50 border-amber-200",             text: "text-amber-900",  label: "text-amber-700", icon: "bg-amber-100 text-amber-700" },
  orange:  { card: "bg-orange-50 border-orange-200",           text: "text-orange-900", label: "text-orange-700",icon: "bg-orange-100 text-orange-700" },
  red:     { card: "bg-red-50 border-red-200",                 text: "text-red-900",    label: "text-red-700",   icon: "bg-red-100 text-red-600" },
  darkred: { card: "bg-red-950 border-red-900",                text: "text-white",      label: "text-red-200",   icon: "bg-red-900 text-red-100" },
  blue:    { card: "bg-blue-50 border-blue-200",               text: "text-blue-900",   label: "text-blue-700",  icon: "bg-blue-100 text-blue-600" },
};

export default function AdminKpiCard({
  label,
  value,
  icon: Icon,
  tone = "slate",
  sublabel,
  trend,
  loading = false,
  onClick,
  testid,
}) {
  const s = TONE_STYLES[tone] || TONE_STYLES.slate;
  const Element = onClick ? "button" : "div";

  if (loading) {
    return (
      <div
        className={`h-full rounded-xl border ${s.card} p-4 flex flex-col justify-between animate-pulse`}
        data-testid={testid ? `${testid}-skeleton` : "kpi-card-skeleton"}
      >
        <div className="h-3 w-24 bg-slate-200 rounded"></div>
        <div className="h-6 w-16 bg-slate-200 rounded mt-4"></div>
      </div>
    );
  }

  return (
    <Element
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-busy={loading}
      aria-label={typeof value === "string" || typeof value === "number" ? `${label}: ${value}` : label}
      className={
        `h-full w-full text-left rounded-xl border ${s.card} p-4 flex flex-col justify-between ` +
        `shadow-brand-soft transition-all duration-200 ` +
        (onClick ? "hover:-translate-y-0.5 hover:shadow-brand-card cursor-pointer" : "")
      }
      data-testid={testid || "kpi-card"}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`text-[10px] font-chivo font-bold uppercase tracking-[0.12em] ${s.label} leading-tight`}>
          {label}
        </div>
        {Icon ? (
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${s.icon}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
        ) : null}
      </div>
      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <div className={`text-xl sm:text-2xl font-chivo font-bold ${s.text} font-mono-num tabular-nums leading-none`}>
          {value ?? "—"}
        </div>
        {trend ? (
          <span
            className={
              `text-[11px] font-semibold ${
                trend.direction === "up"
                  ? "text-emerald-600"
                  : trend.direction === "down"
                    ? "text-red-600"
                    : "text-ink-400"
              }`
            }
            title={trend.label}
          >
            {trend.direction === "up" ? "▲" : trend.direction === "down" ? "▼" : "•"} {trend.pct != null ? `${trend.pct}%` : ""}
          </span>
        ) : null}
      </div>
      {sublabel ? (
        <div className={`text-[11px] mt-1.5 ${s.label} truncate`} title={sublabel}>
          {sublabel}
        </div>
      ) : null}
    </Element>
  );
}
